import React from 'react';

import { Box } from '@components/Restyle';
import { Skeleton } from '@components/shared/Skeleton';
import theme from '@themes/theme';

/**
 * Activity log row placeholder: 40px avatar + name/time line + message
 * line. Mirrors the real web row (ActivityItem.web.tsx: 40px avatar,
 * name+time inline on one line, message below, divider under the text
 * column only) — not the native 32px-avatar/trailing-chip layout the
 * previous version copied. Used for both project Activity Log and
 * Global/Task Activity Log.
 */
export const ActivityRowSkeleton: React.FC<{ testID?: string }> = ({
  testID = 'activity-row-skeleton',
}) => {
  return (
    <Box
      testID={testID}
      flexDirection='row'
      alignItems='flex-start'
      paddingVertical='s'
      paddingHorizontal='m'>
      <Skeleton
        variant='circle'
        width={40}
        height={40}
        style={{ marginRight: theme.spacing.s }}
      />
      <Box flex={1} borderBottomWidth={1} borderBottomColor='grey02' pb='s'>
        <Box
          flexDirection='row'
          alignItems='center'
          marginBottom='xxxs'
          style={{ gap: theme.spacing.xs }}>
          <Skeleton variant='text' width='30%' />
          <Skeleton variant='text' width={50} />
        </Box>
        <Skeleton variant='text' width='65%' />
      </Box>
    </Box>
  );
};

/**
 * Shared `SkeletonList` `row` renderer for `ActivityRowSkeleton` — mirrors
 * the native file's export of the same name (typescript:S6478, TA-10796).
 * Jest's haste platform resolution here has no `web` entry
 * (`react-native/jest-preset`'s `haste.platforms` is
 * `['android', 'ios', 'native']`), and `tsconfig.json` has no
 * `moduleSuffixes` for `.web`, so neither Jest nor `tsc` can ever catch a
 * missing export on this file — this must be kept in sync with
 * `ActivityRowSkeleton.tsx`'s export by hand (PR #5960 review).
 */
export const renderActivityRowSkeleton = (index: number) => (
  <ActivityRowSkeleton key={index} />
);
